/**
 * Excelimport in drie stappen (US-004, US-005, design-handoff scherm 8).
 *
 * Stap 1 kiest een bestand, stap 2 toont de voorvertoning met per rij een status, stap 3
 * bevestigt. Zolang de voorvertoning niet is bevestigd, is er niets gewijzigd.
 */

import { IMPORT_ROW_STATUS_LABELS, type ImportRowStatus } from '@prijsmonitor/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { ApiError, api } from '../api/client.js';
import { Button, Card, Chip, Counter, Notice, ui } from '../components/ui/index.js';
import styles from './ImportScreen.module.css';

interface PreviewRow {
  rowIndex: number;
  status: ImportRowStatus;
  reason: string | null;
  name: string | null;
  ownSku: string | null;
  lengthLabel: string | null;
  color: string | null;
  ean: string | null;
  ownShopUrl: string | null;
}

interface Preview {
  batchId: string;
  filename: string;
  counts: Record<ImportRowStatus, number>;
  warnings: string[];
  recognisedColumns: string[];
  rows: PreviewRow[];
}

/** Statuskleuren volgen de handoff: toevoegen blauw, bestaat al grijs, duplicaat amber, afgewezen rood. */
const STATUS_TONE: Record<ImportRowStatus, 'info' | 'muted' | 'warn' | 'danger' | 'success'> = {
  ADD: 'info',
  UPDATE: 'success',
  EXISTS: 'muted',
  DUPLICATE: 'warn',
  REJECTED: 'danger',
};

/** Beschrijft wat de knop gaat doen: toevoegen, bijwerken of beide. */
function doorvoerLabel(toevoegen: number, bijwerken: number): string {
  if (toevoegen + bijwerken === 0) return 'Niets om door te voeren';
  const delen = [
    toevoegen > 0 ? `${toevoegen} ${toevoegen === 1 ? 'product' : 'producten'} toevoegen` : null,
    bijwerken > 0 ? `${bijwerken} bijwerken` : null,
  ].filter(Boolean);
  return delen.join(' en ');
}

export function ImportScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [result, setResult] = useState<{
    created: number;
    updated: number;
    skipped: number;
  } | null>(null);
  /**
   * Of bestaande producten mogen worden bijgewerkt. Standaard uit: een import hoort nooit
   * ongemerkt bestaande gegevens te wijzigen (US-004/005).
   */
  const [bijwerken, setBijwerken] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);

  async function uploadFile(file: File): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const body = new FormData();
      // De keuze vóór het bestand, zodat de server hem al kent bij het inlezen.
      body.append('bijwerken', bijwerken ? '1' : '0');
      body.append('file', file);
      const response = await fetch('/api/import/voorvertoning', {
        method: 'POST',
        body,
        credentials: 'same-origin',
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new ApiError(
          response.status,
          payload?.error?.code ?? 'ERROR',
          payload?.error?.message ?? 'Het bestand kon niet worden gelezen.',
        );
      }
      setPreview(payload as Preview);
      setResult(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Het bestand kon niet worden verwerkt.');
    } finally {
      setBusy(false);
    }
  }

  async function commit(): Promise<void> {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await api.post(`/api/import/${preview.batchId}/doorvoeren`));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'De import kon niet worden doorgevoerd.');
    } finally {
      setBusy(false);
    }
  }

  async function cancel(): Promise<void> {
    if (!preview) return;
    setBusy(true);
    try {
      await api.post(`/api/import/${preview.batchId}/annuleren`);
    } catch {
      // Een mislukte annulering is niet erg: er is toch niets aangemaakt.
    } finally {
      setPreview(null);
      setResult(null);
      setBusy(false);
    }
  }

  // --- Stap 3: bevestiging -------------------------------------------------
  if (result) {
    return (
      <div className={ui.page}>
        <Card>
          <Notice tone="success">
            {result.created === 0
              ? 'Er zijn geen nieuwe producten aangemaakt.'
              : `${result.created} ${result.created === 1 ? 'nieuw product' : 'nieuwe producten'} aangemaakt.`}
            {result.updated > 0 &&
              ` ${result.updated} bestaand${result.updated === 1 ? ' product is' : 'e producten zijn'} bijgewerkt.`}
            {result.skipped > 0 &&
              ` ${result.skipped} ${result.skipped === 1 ? 'rij is' : 'rijen zijn'} overgeslagen omdat het artikelnummer al bestond.`}
          </Notice>
          <p className={ui.cardSub} style={{ marginTop: 'var(--space-12)' }}>
            Nieuwe producten staan op concept. Vul waar nodig de lengte en de kritische kenmerken
            aan en koppel daarna de prijsbronnen.
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-8)', marginTop: 'var(--space-16)' }}>
            <Button variant="primary" onClick={() => void navigate('/bronconfigurator')}>
              Naar de bronconfigurator
            </Button>
            <Button
              onClick={() => {
                setPreview(null);
                setResult(null);
              }}
            >
              Nog een bestand importeren
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // --- Stap 2: voorvertoning -----------------------------------------------
  if (preview) {
    return (
      <div className={ui.page}>
        {error && <Notice tone="error">{error}</Notice>}
        {preview.warnings.map((warning) => (
          <Notice key={warning} tone="warn">
            {warning}
          </Notice>
        ))}

        <div className={ui.counterRow}>
          <Counter value={preview.counts.ADD} label="Toevoegen" />
          {bijwerken && <Counter value={preview.counts.UPDATE} label="Bijwerken" />}
          <Counter value={preview.counts.EXISTS} label="Ongewijzigd" />
          <Counter value={preview.counts.DUPLICATE} label="Duplicaat" tone="warn" />
          <Counter value={preview.counts.REJECTED} label="Afgewezen" tone="danger" />
        </div>

        <Card
          title={`Voorvertoning · ${preview.filename}`}
          description={
            bijwerken
              ? 'Er is nog niets gewijzigd. Bij het doorvoeren worden bestaande producten bijgewerkt met de kolommen die in het bestand staan; lege kolommen laten de huidige waarde staan.'
              : 'Er is nog niets gewijzigd. Bestaande producten blijven ongemoeid. Wilt u ze bijwerken, annuleer dan en vink dat aan voor het uploaden.'
          }
        >
          <div className={ui.tableWrap}>
            <table className={ui.table}>
              <thead>
                <tr>
                  <th className={ui.numeric}>Rij</th>
                  <th>Status</th>
                  <th>Productnaam</th>
                  <th>Artikelnummer</th>
                  <th>Lengte</th>
                  <th>Kleur</th>
                  <th>EAN</th>
                  <th>Webshop-URL</th>
                  <th>Toelichting</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => (
                  <tr key={row.rowIndex}>
                    <td className={ui.numeric}>{row.rowIndex}</td>
                    <td>
                      <Chip tone={STATUS_TONE[row.status]}>
                        {IMPORT_ROW_STATUS_LABELS[row.status]}
                      </Chip>
                    </td>
                    <td>{row.name ?? '—'}</td>
                    <td>{row.ownSku ?? '—'}</td>
                    <td>{row.lengthLabel ?? '—'}</td>
                    <td>{row.color ?? '—'}</td>
                    <td>{row.ean ?? '—'}</td>
                    <td className={styles.url}>
                      {row.ownShopUrl ? (
                        <a href={row.ownShopUrl} target="_blank" rel="noreferrer noopener">
                          {row.ownShopUrl.replace(/^https?:\/\//, '')}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={styles.reason}>{row.reason ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.actions}>
            <Button onClick={() => void cancel()} busy={busy}>
              Annuleren
            </Button>
            <Button
              variant="primary"
              onClick={() => void commit()}
              busy={busy}
              disabled={preview.counts.ADD + preview.counts.UPDATE === 0}
            >
              {doorvoerLabel(preview.counts.ADD, preview.counts.UPDATE)}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // --- Stap 1: bestand kiezen ----------------------------------------------
  return (
    <div className={ui.page}>
      {error && <Notice tone="error">{error}</Notice>}

      <Card
        title="Producten importeren"
        description="Lever een Excelbestand aan met op het eerste tabblad een kolom productnaam en een kolom artikelnummer."
      >
        <p className={ui.hint} style={{ marginBottom: 'var(--space-12)' }}>
          Herkende kolomkoppen — hoofdletters, spaties en streepjes maken niet uit:{' '}
          <strong>productnaam</strong> of naam, <strong>artikelnummer</strong> of sku,{' '}
          <strong>lengte</strong>, <strong>kleur</strong>, <strong>kleur kritisch</strong>,{' '}
          <strong>folie</strong>, <strong>folie kritisch</strong>, <strong>EAN</strong> of barcode,{' '}
          <strong>fabrikantartikelnummer</strong> of mpn, en <strong>URL</strong> of webshop-url
          voor de eigen productpagina. Onbekende kolommen worden overgeslagen.
        </p>

        <label className={styles.bijwerkKeuze}>
          <input
            type="checkbox"
            checked={bijwerken}
            onChange={(e) => setBijwerken(e.target.checked)}
          />
          <span>
            <strong>Bestaande producten bijwerken</strong>
            <span className={ui.hint}>
              Standaard uit: dan worden alleen nieuwe artikelnummers aangemaakt. Aan betekent dat
              een bestaand product de waarden uit het bestand overneemt — je ziet in de
              voorvertoning per regel wat er zou veranderen voordat je iets bevestigt. Lege kolommen
              wijzigen niets.
            </span>
          </span>
        </label>

        <label
          className={dragging ? styles.dropzoneActive : styles.dropzone}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const file = e.dataTransfer.files[0];
            if (file) void uploadFile(file);
          }}
        >
          <input
            type="file"
            accept=".xlsx"
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void uploadFile(file);
              e.target.value = '';
            }}
          />
          {busy ? (
            <>
              <span className="spinner" aria-hidden="true" />
              <span className={styles.dropzoneText}>Bestand wordt gecontroleerd…</span>
            </>
          ) : (
            <>
              <span className={styles.dropzoneTitle}>Sleep een Excelbestand hierheen</span>
              <span className={styles.dropzoneText}>of klik om een bestand te kiezen (.xlsx)</span>
            </>
          )}
        </label>

        <p className={ui.hint} style={{ marginTop: 'var(--space-12)' }}>
          Na het kiezen ziet u eerst een voorvertoning. Producten die niet in het bestand voorkomen
          worden nooit gearchiveerd of verwijderd.
        </p>
      </Card>
    </div>
  );
}
